import {
  Icon,
  Input,
  Spinner,
  IconButton,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Box,
  Text,
  PopoverTrigger,
  useDisclosure,
  HStack,
  Button,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { SearchOutlined, Cancel } from "@mui/icons-material";
import { useInfiniteSearchByPartNumber } from "../queryHooks";
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import GaraazGeneralPopover from "./GaraazGeneralPopover";
import services from "./../services";
import { debounce as lodashDebounce } from "lodash";
import useNotification from "../../../utils/useNotification";
import { useOrdersStore } from "../store/useOrdersStore";
import GaraazPartConversionModal from "./GaraazPartConversionModal";
import FacetedPartSearch from "../../../components/FacetedPartSearch";
import { RiArrowRightUpLine } from "react-icons/ri";
import { FaSyncAlt, FaPause, FaPlay, FaTimes, FaPen } from "react-icons/fa";
import { usePartAvailability } from "../hooks/usePartAvailability";
import { usePartAvailabilityStore } from "../store/usePartAvailabilityStore";
import PartActionModal from "./PartActionModal";
import AddPartQuantityModal from "./AddPartQuantityModal";
import { OrderStatus, Roles, BillingEntity } from "../enums";
import { useUserDetailsContext } from "../../../context/userDetailsContext";
import { useGetOrderMediaConfig } from "../../PlaceOrderTest/queryHooks";
import { finalOrcaStates } from "./GaraazOrderComponent";
import { isAxiosError } from "axios";

type Props = {
  orderId: string;
  findBrandName: (brandId: string) => any;
  isDisabled: boolean;
  orderData: any;
  componentHeight?: string;
  customerGeoPoint?: { latitude: number; longitude: number };
  refetchAllOrders?: () => void;
};

const GaraazPartConversion = (props: Props) => {
  const toast = useNotification();
  const userDetails = useUserDetailsContext();
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const {
    orderId,
    findBrandName,
    isDisabled,
    orderData,
    customerGeoPoint,
    refetchAllOrders,
  } = props;
  const [PartSearchValue, setPartSearchValue] = useState("");
  const [debouncedPartSearchValue, setDebouncedPartSearchValue] = useState("");

  const [partSearchResultBoxDisplay, setPartSearchResultBoxDisplay] =
    useState("none");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isFacetedPartSearchOpen,
    onOpen: onFacetedPartSearchOpen,
    onClose: onFacetedPartSearchClose,
  } = useDisclosure();
  const {
    isOpen: isCancelHoldModalOpen,
    onOpen: onCancelHoldModalOpen,
    onClose: onCancelHoldModalClose,
  } = useDisclosure();
  const {
    isOpen: isQuantityModalOpen,
    onOpen: onQuantityModalOpen,
    onClose: onQuantityModalClose,
  } = useDisclosure();
  const [actionMode, setActionMode] = useState<
    "cancel" | "hold" | "unhold" | "add" | null
  >(null);
  const [selectedPartIndex, setSelectedPartIndex] = useState<number | null>(
    null,
  );
  const [quantity, setQuantity] = useState<string>("1");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: infiniteParts,
    isLoading: partsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteSearchByPartNumber(debouncedPartSearchValue);

  const parts = useMemo(() => {
    return infiniteParts?.pages.flatMap((page) => page) || [];
  }, [infiniteParts]);

  const handleScroll = (e: any) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };
  const orderItems = useOrdersStore((state) => state.orderItems[orderId]);
  const setOrderItemsWithOrderId = useOrdersStore(
    (state) => state.setOrderItemsWithOrderId,
  );

  const [isPartEditDisabled, setIsPartEditDisabled] = useState(false);

  useEffect(() => {
    const orderStatus = orderData.orderStatus;
    const isRootOrder = orderData._id === orderData.rootOrderId;
    setIsPartEditDisabled(
      !(orderData?.requireEstimate && isRootOrder) ||
        orderData.orderType === "RETURN" ||
        orderData.orderType === "SUBSCRIPTION" ||
        finalOrcaStates.includes(orderStatus) ||
        orderStatus === OrderStatus.READY_TO_DISPATCH ||
        orderStatus === OrderStatus.DELIVERED,
    );
  }, [userDetails?.userSubType, orderData, mobileConfigs]);

  const isInsuranceEnquiryOrder = useMemo(() => {
    const isEnquiryOrder =
      orderData?.requireEstimate === true &&
      orderData?.rootOrderId === orderData?._id;
    const isInsuranceEntity =
      orderData?.billingDetails?.billingEntity ===
      BillingEntity.INSURANCE_COMPANY;
    const insurerFamily =
      orderData?.billingAddress?.billingCustomerInsurerFamily ??
      orderData?.billingDetails?.billingCustomerInsurerFamily;
    const eligibleFamilies =
      mobileConfigs?.eligible_insurer_families_for_approval ?? [];
    const isEligibleForApproval =
      Array.isArray(eligibleFamilies) &&
      eligibleFamilies.length > 0 &&
      typeof insurerFamily === "string" &&
      eligibleFamilies.some(
        (f: string) =>
          String(f).trim().toLowerCase() ===
          String(insurerFamily).trim().toLowerCase(),
      );
    return isEnquiryOrder && isInsuranceEntity && isEligibleForApproval;
  }, [
    orderData?.requireEstimate,
    orderData?.rootOrderId,
    orderData?._id,
    orderData?.billingDetails?.billingEntity,
    orderData?.billingAddress?.billingCustomerInsurerFamily,
    orderData?.billingDetails?.billingCustomerInsurerFamily,
    mobileConfigs?.eligible_insurer_families_for_approval,
  ]);

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      debouncedUpdatePartRef.current.forEach((debouncedFunc) => {
        if (debouncedFunc && typeof debouncedFunc.cancel === "function") {
          debouncedFunc.cancel();
        }
      });
      debouncedUpdatePartRef.current.clear();
    };
  }, []);

  const [alreadyExistsError, setAlreadyExistsError] = useState<any[]>([]);
  const [isRefetching, setIsRefetching] = useState(false);
  const debouncedUpdatePartRef = useRef<
    Map<string, ReturnType<typeof lodashDebounce>>
  >(new Map());
  const {
    isAvailabilityColumnVisible,
    setAvailabilityColumnVisible,
    getAvailabilityData,
  } = usePartAvailabilityStore();

  const showAvailabilityColumn = isAvailabilityColumnVisible(orderId);

  const {
    availabilityMap,
    availabilityLoading,
    fetchAvailability,
    lastFetchTime,
  } = usePartAvailability({
    orderId,
    orderItems,
    customerGeoPoint,
    enabled: false,
  });

  const debounce = useCallback((value: string) => {
    const timer = setTimeout(() => {
      setDebouncedPartSearchValue(value);
    }, 0);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const openAvailabilityPage = () => {
    try {
      const partsForAvailabilityCheck = orderItems.map((item: any) => ({
        rootPartNumber: item.rootPartNumber,
        partNumber: item.partNumber,
        quantity: item.remainingDecisionQuantity,
        partName: item.partName,
      }));

      const searchCriteria = {
        parts: partsForAvailabilityCheck,
        customerGeoPoint: customerGeoPoint,
        orderNumber: orderData?.orderNo,
      };
      const url = `/part-availability?criteria=${encodeURIComponent(
        JSON.stringify(searchCriteria),
      )}`;
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error opening availability page:", error);
    }
  };

  useEffect(() => {
    setAlreadyExistsError(Array(parts?.length).fill(""));
  }, [parts]);
  useEffect(() => {
    return debounce(PartSearchValue);
  }, [PartSearchValue, debounce]);

  useEffect(() => {
    const hasData =
      getAvailabilityData(orderId) && getAvailabilityData(orderId)!.length > 0;
    if (hasData && !showAvailabilityColumn) {
      setAvailabilityColumnVisible(orderId, true);
    }
  }, [
    orderId,
    showAvailabilityColumn,
    getAvailabilityData,
    setAvailabilityColumnVisible,
  ]);

  useEffect(() => {
    if (availabilityMap.size > 0 && isRefetching) {
      setIsRefetching(false);
    }
  }, [availabilityMap, isRefetching]);

  function handlePartSearchValueChange(e: React.ChangeEvent<HTMLInputElement>) {
    setPartSearchValue(e.target.value);
  }

  function handlePartClick(index: number) {
    if (isPartEditDisabled) return;

    if (parts) {
      const alreadyExists = orderItems.some(
        (part: any) => part.partId === parts[index]._id,
      );
      if (alreadyExists) {
        setAlreadyExistsError((previousAlreadyExistsError) => {
          const newArray = [...previousAlreadyExistsError];
          newArray[index] = "Already Exists";
          return newArray;
        });
        return;
      }

      // Open quantity modal
      setSelectedPartIndex(index);
      setQuantity("1");
      onQuantityModalOpen();
    }
  }

  async function handleAddPartWithQuantity() {
    if (selectedPartIndex === null || parts === undefined) return;

    const quantityNum = parseInt(quantity);

    // Validate quantity
    if (isNaN(quantityNum) || quantityNum < 1) {
      toast({
        description: "Quantity must be at least 1",
        status: "error",
      });
      return;
    }

    const part = parts[selectedPartIndex];
    const alreadyExists = orderItems.some(
      (item: any) => item.partId === part._id,
    );

    if (alreadyExists) {
      toast({
        description: "Part already exists in order",
        status: "error",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await services.orderItemsAction(orderData.orderNo, "ADD", [
        {
          partId: part._id,
          quantity: quantityNum,
        },
      ]);
      setOrderItemsWithOrderId(orderId, [
        {
          ...part,
          partId: part._id,
          orderedQuantity: quantityNum,
          remainingDecisionQuantity: quantityNum,
          holdQuantity: 0,
          cancelledQuantity: 0,
          givenQuantity: 0,
        },
        ...orderItems,
      ]);
      setPartSearchResultBoxDisplay("none");
      setPartSearchValue("");
      onQuantityModalClose();
      setSelectedPartIndex(null);
      setQuantity("1");
      refetchAllOrders?.();
    } catch (error) {
      let errorMessage = "There was an error in Inserting the part, please try again";
      if (isAxiosError(error)) {
        errorMessage =
          error.response?.data?.statusCode === 409
            ? error.response?.data?.errorMsg
            : errorMessage;
      }
      toast({
        description: errorMessage,
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }
  async function handleAddPartFromPartSearch_2_0(
    part: any,
    quantityNum: number,
  ) {
    await services.orderItemsAction(orderData.orderNo, "ADD", [
      {
        partId: part._id || part.partId,
        quantity: quantityNum,
      },
    ]);
    refetchAllOrders?.();
  }

  async function handleDeleteRow(part: any) {
    if (isDisabled) return;

    try {
      const updatedPart = await services.deletePart(orderId, {
        partId: part.partId,
        orderedQuantity: part.orderedQuantity,
      });

      let updatedOrderItems = [...orderItems];
      const partIndex = updatedOrderItems.findIndex(
        (item) =>
          item.partId === part.partId ||
          (item.partNumber === part.partNumber &&
            item.rootPartNumber === part.rootPartNumber),
      );

      if (partIndex !== -1) {
        updatedOrderItems.splice(partIndex, 1);
        setOrderItemsWithOrderId(orderId, updatedOrderItems);
      }
    } catch (error: any) {
      toast({
        description:
          "There was an error in deleting the part, please try again",
        status: "error",
      });
    }
  }
  function remove_idFromPartDetails(partObject: any) {
    return {
      "Part Name": partObject?.partName,
      "Part Number": partObject.partNumber,
      MRP: partObject.mrp,
      Brand: partObject.brandId
        ? findBrandName(partObject.brandId)
        : partObject.brand,
      "HSN code": partObject.hsnCode,
      "Root Part Number": partObject.rootPartNumber,
    };
  }
  function handleQuanityValueChange(
    e: React.ChangeEvent<HTMLInputElement>,
    part: any,
  ) {
    if (isDisabled) return;

    try {
      let updatedOrderItems = [...orderItems];
      const partIndex = updatedOrderItems.findIndex(
        (item) =>
          item.partId === part.partId ||
          (item.partNumber === part.partNumber &&
            item.rootPartNumber === part.rootPartNumber),
      );

      if (partIndex !== -1) {
        updatedOrderItems[partIndex].orderedQuantity = parseInt(e.target.value);
        if (parseInt(e.target.value) > 0) {
          updatedOrderItems[partIndex].remainingDecisionQuantity = parseInt(
            e.target.value,
          );
        }
        setOrderItemsWithOrderId(orderId, updatedOrderItems);

        // Get or create debounced function for this part
        if (!debouncedUpdatePartRef.current.has(part.partId)) {
          debouncedUpdatePartRef.current.set(
            part.partId,
            lodashDebounce(async (partId: string, quantity: number) => {
              const partData = {
                partId: partId,
                orderedQuantity: quantity,
                remainingDecisionQuantity: quantity,
              };
              if (partData.orderedQuantity > 0) {
                await services.insertOrUpdatePart(orderId, partData);
              }
            }, 2000),
          );
        }

        const debouncedFunc = debouncedUpdatePartRef.current.get(part.partId);
        if (e.target.value !== "" && e.target.value !== "0" && debouncedFunc) {
          debouncedFunc(part.partId, parseInt(e.target.value));
        }
      }
    } catch (error) {
      toast({
        description:
          "There was an error in updating the part, please try again ",
        status: "error",
      });
    }
  }

  const handleRefetchAvailabilityClick = () => {
    setIsRefetching(true);
    setAvailabilityColumnVisible(orderId, true);
    fetchAvailability().finally(() => {
      setIsRefetching(false);
    });
  };

  const sortRemainingDecisionQuantity = (arr?: any[]) => {
    return arr?.sort(
      (a, b) =>
        (b.remainingDecisionQuantity||0) +
        (b.holdQuantity||0) -
        ((a.remainingDecisionQuantity||0) + (a.holdQuantity||0)),
    );
  };

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      flex={1}
      width={"100%"}
      maxHeight={props.componentHeight || "100%"}
      style={{ margin: 0 }}
    >
      <Box display={"flex"} alignItems={"center"} width={"100%"} height={"10%"}>
        <Icon mr={2}>
          <SearchOutlined fontSize="large"></SearchOutlined>
        </Icon>
        <Box position={"relative"} width={"100%"}>
          <Box display={"flex"} position={"relative"} alignItems={"center"}>
            <Input
              isDisabled={isPartEditDisabled}
              id={`part-search-input-${orderId}`}
              value={PartSearchValue}
              size={"sm"}
              placeholder="Search By Part Number Or Name"
              onChange={(e) => {
                handlePartSearchValueChange(e);
                setPartSearchResultBoxDisplay("flex");
              }}
              borderRadius={"10px"}
            />
            {partsLoading && (
              <Spinner position={"absolute"} right={2} top={1}></Spinner>
            )}

            <Icon
              fontSize={"2xl"}
              onClick={isPartEditDisabled ? undefined : onFacetedPartSearchOpen}
              cursor={isPartEditDisabled ? "not-allowed" : "pointer"}
              opacity={isPartEditDisabled ? 0.6 : 1}
            >
              <RiArrowRightUpLine fontWeight={"bold"}></RiArrowRightUpLine>
            </Icon>
          </Box>

          <Box
            display={`${partSearchResultBoxDisplay}`}
            position={"absolute"}
            width={"100%"}
            height={"200px"}
            backgroundColor={"white"}
            flexDirection={"column"}
            overflow={"auto"}
            border={"1px solid lightgrey"}
            borderRadius={"0px 0px 5px 5px"}
            zIndex={1000}
            onScroll={handleScroll}
          >
            <IconButton
              width={"fit-content"}
              icon={<Cancel fontSize="small"></Cancel>}
              aria-label=""
              position={"sticky"}
              size={"xs"}
              right={"10px"}
              top={0}
              marginTop={"2px"}
              marginLeft={"auto"}
              onClick={() => {
                setPartSearchResultBoxDisplay("none");
              }}
            ></IconButton>

            <Box padding={"10px"}>
              {partsLoading && <Text>Loading...</Text>}

              {!partsLoading &&
                parts?.map((part: any, index: number) => (
                  <Box
                    key={index}
                    display={"flex"}
                    mb={2}
                    padding={"5px"}
                    borderRadius={"5px"}
                    _hover={
                      !isPartEditDisabled ? { backgroundColor: "#F0F6F6" } : {}
                    }
                    transition={"all 0.3s"}
                    onClick={() => {
                      if (!isPartEditDisabled) handlePartClick(index);
                    }}
                    cursor={isPartEditDisabled ? "not-allowed" : "pointer"}
                    border={"0.5px solid lightgrey"}
                    opacity={isPartEditDisabled ? 0.6 : 1}
                  >
                    <Box>
                      <Text>{part.partNumber}</Text>
                      <Text noOfLines={1}>{part.partName}</Text>
                    </Box>

                    {alreadyExistsError[index] !== "" && (
                      <Text color={"red"} marginLeft={"auto"} mr={5}>
                        {alreadyExistsError[index]}
                      </Text>
                    )}
                  </Box>
                ))}
              {isFetchingNextPage && (
                <Spinner size="sm" m="auto" mt={2} mb={2} />
              )}
              {(!parts || parts?.length === 0) && !partsLoading && (
                <Text>No Options </Text>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        mt={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pl={1}
        bg="gray.50"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
        position="relative"
      >
        {(availabilityLoading || isRefetching) && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(255, 255, 255, 0.8)"
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={1}
          >
            <HStack spacing={2}>
              <Spinner size="sm" color="blue.500" />
              <Text fontSize="x-small" color="blue.600" fontWeight="500">
                Fetching availability data...
              </Text>
            </HStack>
          </Box>
        )}
        <HStack spacing={0}>
          <Text fontSize="x-small" fontWeight="500" color="gray.700">
            📊 Fetch Availability
          </Text>
          <IconButton
            size="sm"
            color="blue.500"
            icon={
              availabilityLoading || isRefetching ? (
                <Spinner size="xs" />
              ) : (
                <Icon as={FaSyncAlt} />
              )
            }
            bg="transparent"
            aria-label="Refetch part availability"
            onClick={handleRefetchAvailabilityClick}
            isDisabled={
              availabilityLoading || isRefetching || orderItems.length === 0
            }
            transition="all 0.2s"
            _hover={{
              bg: "transparent",
              transform: "scale(1.1)",
            }}
          />
          {showAvailabilityColumn && (
            <HStack spacing={2}>
              {lastFetchTime && (
                <Text fontSize="x-small" color="gray.600" fontWeight="500">
                  Last updated: {lastFetchTime.toLocaleString()}
                </Text>
              )}
            </HStack>
          )}
        </HStack>
        <HStack spacing={2}>
          {orderData.requireEstimate && (
            <>
              <Tooltip label="Add Quantity">
                <IconButton
                  icon={<FaPen />}
                  backgroundColor={"#3182CE"}
                  color={"white"}
                  aria-label={""}
                  onClick={() => {
                    setActionMode("add");
                    onCancelHoldModalOpen();
                  }}
                  padding={0.5}
                  aspectRatio={"1/1"}
                  height={"100%"}
                  size="xs"
                  fontSize="xs"
                  isDisabled={orderItems.length === 0 || isPartEditDisabled}
                  _hover={{
                    backgroundColor: "#3182CE",
                  }}
                />
              </Tooltip>
              <Tooltip label="Cancel Items">
                <IconButton
                  icon={<FaTimes />}
                  backgroundColor={"#FF0000"}
                  color={"white"}
                  aria-label={""}
                  onClick={() => {
                    setActionMode("cancel");
                    onCancelHoldModalOpen();
                  }}
                  padding={0.5}
                  aspectRatio={"1/1"}
                  height={"100%"}
                  size="xs"
                  fontSize="xs"
                  isDisabled={
                    orderItems.length === 0 ||
                    orderItems.every(
                      (item: any) => item.remainingDecisionQuantity === 0,
                    )
                  }
                  _hover={{
                    backgroundColor: "#FF0000",
                  }}
                />
              </Tooltip>

              <Tooltip label="Hold Items">
                <IconButton
                  icon={<FaPause />}
                  backgroundColor={"#FFA500"}
                  color={"white"}
                  aria-label={""}
                  onClick={() => {
                    setActionMode("hold");
                    onCancelHoldModalOpen();
                  }}
                  padding={0.5}
                  aspectRatio={"1/1"}
                  height={"100%"}
                  size="xs"
                  fontSize="2xs"
                  isDisabled={
                    orderItems.length === 0 ||
                    orderItems.every(
                      (item: any) => item.remainingDecisionQuantity === 0,
                    )
                  }
                  _hover={{
                    backgroundColor: "#FFA500",
                  }}
                />
              </Tooltip>
              <Tooltip label="Re-Activate Items">
                <IconButton
                  icon={<FaPlay />}
                  backgroundColor={" #02b002"}
                  color={"white"}
                  aria-label={""}
                  onClick={() => {
                    setActionMode("unhold");
                    onCancelHoldModalOpen();
                  }}
                  padding={0.5}
                  aspectRatio={"1/1"}
                  height={"100%"}
                  size="xs"
                  fontSize="2xs"
                  isDisabled={
                    orderItems.length === 0 ||
                    orderItems.every((item: any) => item.holdQuantity === 0)
                  }
                  _hover={{
                    backgroundColor: "#02b002",
                  }}
                />
              </Tooltip>
            </>
          )}
          <Button
            size="xs"
            fontSize="2xs"
            variant="solid"
            colorScheme="blue"
            onClick={openAvailabilityPage}
            borderRadius="md"
            disabled={orderItems.length === 0}
          >
            Detailed Analysis
          </Button>
        </HStack>
      </Box>

      <Box
        width={"100%"}
        height={"75%"}
        mt={1}
        overflowY={"scroll"}
        boxSizing="border-box"
      >
        <Table
          variant="simple"
          width={"100%"}
          overflowY={"scroll"}
          height={"100%"}
        >
          <Thead width={"100%"} bg="white" position="sticky" top={0} zIndex={1}>
            <Tr
              padding={"5px"}
              display={"flex"}
              width={"100%"}
              justifyContent={"space-between"}
              alignItems={"center"}
              borderBottom={"1px solid #EDF2F6"}
            >
              <Th border={"none"} padding={"0"} width={"20%"}>
                <Text fontSize={"x-small"}>Part No.</Text>
              </Th>

              <Th
                border={"none"}
                padding={"0"}
                isNumeric
                width={orderData?.orderType === "RETURN" ? "25%" : "15%"}
                textAlign={"center"}
              >
                <Text textAlign={"center"} fontSize={"x-small"}>
                  {orderData?.orderType === "RETURN"
                    ? "Returned Ordered"
                    : "Ordered"}
                </Text>
              </Th>
              {orderData.orderType === "REGULAR" &&
                !orderData.requireEstimate && (
                  <Th
                    border={"none"}
                    padding={"0"}
                    isNumeric
                    width={"15%"}
                    textAlign={"center"}
                  >
                    <Text textAlign={"center"} fontSize={"x-small"}>
                      Given
                    </Text>
                  </Th>
                )}
              {orderData.requireEstimate && (
                <>
                  <Th
                    border={"none"}
                    padding={"0"}
                    isNumeric
                    width={"15%"}
                    textAlign={"center"}
                  >
                    <Text textAlign={"center"} fontSize={"x-small"}>
                      Active
                    </Text>
                  </Th>
                  <Th border={"none"} padding={"0"} isNumeric width={"15%"} textAlign={"center"}>
                    <Tooltip label="Customer Cancelled" hasArrow>
                      <Text textAlign={"center"} fontSize={"x-small"} cursor="pointer">
                        CC
                      </Text>
                    </Tooltip>
                  </Th>
                  <Th
                    border={"none"}
                    padding={"0"}
                    isNumeric
                    width={"15%"}
                    textAlign={"center"}
                  >
                    <Text textAlign={"center"} fontSize={"x-small"}>
                      Hold
                    </Text>
                  </Th>
                  <Th border={"none"} padding={"0"} isNumeric width={"15%"} textAlign={"center"}>
                    <Tooltip label="Supplier Cancelled" hasArrow>
                      <Text textAlign={"center"} fontSize={"x-small"} cursor="pointer">
                        SC
                      </Text>
                    </Tooltip>
                  </Th>
                  {isInsuranceEnquiryOrder && (
                    <>
                      <Th
                        border={"none"}
                        padding={"0"}
                        isNumeric
                        width={"15%"}
                        textAlign={"center"}
                      >
                        <Text textAlign={"center"} fontSize={"x-small"}>
                          Approved
                        </Text>
                      </Th>
                      <Th
                        border={"none"}
                        padding={"0"}
                        isNumeric
                        width={"15%"}
                        textAlign={"center"}
                      >
                        <Text textAlign={"center"} fontSize={"x-small"}>
                          Rejected
                        </Text>
                      </Th>
                    </>
                  )}
                </>
              )}
              {showAvailabilityColumn && (
                <Th
                  border={"none"}
                  padding={"0"}
                  width={"25%"}
                  textAlign={"center"}
                >
                  <Text textAlign={"center"} fontSize={"x-small"}>
                    Availability
                  </Text>
                </Th>
              )}
              <Th
                border={"none"}
                padding={"0"}
                width={"15%"}
                textAlign={"end"}
                cursor={"pointer"}
                mr={"1em"}
              >
                <Text
                  fontSize={"x-small"}
                  color={"#0096e0"}
                  onClick={() => {
                    onOpen();
                  }}
                  textAlign={"center"}
                >
                  {orderItems.length} Parts Added
                </Text>
              </Th>
            </Tr>
          </Thead>
          <Tbody width={"100%"} height={"100%"}>
            {orderItems.length > 0 &&
              Object.entries(orderItems[0])?.length !== 0 &&
              (() => {
                let sortedOrderItems = sortRemainingDecisionQuantity([
                  ...orderItems,
                ]);

                if (showAvailabilityColumn) {
                  sortedOrderItems?.sort((a, b) => {
                    const availabilityA = availabilityMap.get(a.rootPartNumber);
                    const availabilityB = availabilityMap.get(b.rootPartNumber);
                    if (availabilityA && availabilityB) {
                      const percentageDiff =
                        availabilityB.quantityFulfilledPercentage -
                        availabilityA.quantityFulfilledPercentage;

                      if (percentageDiff === 0) {
                        return (
                          availabilityB.quantityFulfilledCount -
                          availabilityA.quantityFulfilledCount
                        );
                      }

                      return percentageDiff;
                    }

                    if (availabilityA && !availabilityB) return -1;
                    if (!availabilityA && availabilityB) return 1;

                    return 0;
                  });
                }

                return sortedOrderItems?.map((part: any, index: number) => {
                  const availability = availabilityMap.get(part.rootPartNumber);

                  return (
                    <Tr
                      key={`${part.partNumber}-${index}`}
                      padding={"2px"}
                      fontSize={"x-small"}
                      width={"100%"}
                      textColor={"black"}
                      display={"flex"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      onMouseEnter={() => {
                        setHoveredIndex(index);
                      }}
                      onMouseLeave={() => {
                        setHoveredIndex(null);
                      }}
                      _hover={{
                        backgroundColor: "#F0F6F6",
                      }}
                      transition="all 0.2s"
                    >
                      <Td padding={"0"} width={"20%"} border={"none"}>
                        <GaraazGeneralPopover
                          ArekeysVisible={true}
                          CopierKeys={[]}
                          title="Part Details"
                          value={[remove_idFromPartDetails(part)]}
                        >
                          <PopoverTrigger>
                            <Text
                              cursor={"pointer"}
                              noOfLines={1}
                              whiteSpace={"normal"}
                              fontSize={"x-small"}
                              color={"#1a202c"}
                            >
                              {part.partNumber}
                            </Text>
                          </PopoverTrigger>
                        </GaraazGeneralPopover>
                      </Td>

                      {/* <Td padding={"0"} width={"15%"} isNumeric border={"none"}>
                        <Text width={"100%"} textAlign={"center"}>
                          <Input
                            isDisabled={isDisabled}
                            id={`part-quantity-${orderId}-${index}`}
                            backgroundColor={"white"}
                            size={"xs"}
                            type="number"
                            padding={0}
                            width={"50%"}
                            maxWidth={"50px"}
                            zIndex={0}
                            paddingLeft={1}
                            value={part.orderedQuantity}
                            min={1}
                            onChange={(e) => {
                              handleQuanityValueChange(e, part);
                            }}
                            height={"85%"}
                          ></Input>
                        </Text>
                      </Td> */}
                      <Td padding={"0"} width={"15%"} isNumeric border={"none"}>
                        <Text width={"100%"} textAlign={"center"}>
                          {part.orderedQuantity}
                        </Text>
                      </Td>
                      {orderData.orderType === "REGULAR" &&
                        !orderData.requireEstimate && (
                          <Td
                            padding={"0"}
                            width={"15%"}
                            isNumeric
                            border={"none"}
                          >
                            <Text width={"100%"} textAlign={"center"}>
                              {part.givenQuantity || 0}
                            </Text>
                          </Td>
                        )}
                      {orderData.requireEstimate && (
                        <>
                          <Td
                            padding={"0"}
                            width={"15%"}
                            isNumeric
                            border={"none"}
                          >
                            <Text width={"100%"} textAlign={"center"}>
                              {part.remainingDecisionQuantity}
                            </Text>
                          </Td>
                          <Td
                            padding={"0"}
                            width={"15%"}
                            isNumeric
                            border={"none"}
                          >
                            <Text width={"100%"} textAlign={"center"}>
                              {part.cancelledQuantity || 0}
                            </Text>
                          </Td>
                          <Td
                            padding={"0"}
                            width={"15%"}
                            isNumeric
                            border={"none"}
                          >
                            <Text width={"100%"} textAlign={"center"}>
                              {part.holdQuantity || 0}
                            </Text>
                          </Td>
                          <Td
                            padding={"0"}
                            width={"15%"}
                            isNumeric
                            border={"none"}
                          >
                            <Text width={"100%"} textAlign={"center"}>
                              {part.supplierCancelledQuantity || 0}
                            </Text>
                          </Td>
                          {isInsuranceEnquiryOrder && (
                            <>
                              <Td
                                padding={"0"}
                                width={"15%"}
                                isNumeric
                                border={"none"}
                              >
                                <Text width={"100%"} textAlign={"center"}>
                                  {part.approvedQuantity || 0}
                                </Text>
                              </Td>
                              <Td
                                padding={"0"}
                                width={"15%"}
                                isNumeric
                                border={"none"}
                              >
                                <Text width={"100%"} textAlign={"center"}>
                                  {part.rejectedQuantity || 0}
                                </Text>
                              </Td>
                            </>
                          )}
                        </>
                      )}
                      {showAvailabilityColumn && (
                        <Td
                          padding={"0"}
                          width={"25%"}
                          border={"none"}
                          textAlign={"center"}
                        >
                          {availabilityLoading ? (
                            <Spinner size="xs" />
                          ) : availability ? (
                            <Text
                              fontSize="xs"
                              fontWeight="900"
                              color={
                                availability.quantityFulfilledPercentage >= 80
                                  ? "green.500"
                                  : availability.quantityFulfilledPercentage >=
                                      60
                                    ? "yellow.600"
                                    : availability.quantityFulfilledPercentage >=
                                        40
                                      ? "yellow.400"
                                      : availability.quantityFulfilledPercentage >=
                                          20
                                        ? "orange.500"
                                        : "red.500"
                              }
                            >
                              {availability.quantityFulfilledCount}(
                              {availability.quantityFulfilledPercentage}%)
                            </Text>
                          ) : (
                            <Text fontSize="xs" color="gray.500">
                              Not Fetched
                            </Text>
                          )}
                        </Td>
                      )}
                      <Td
                        width={"15%"}
                        textAlign={"end"}
                        padding={"0"}
                        border={"none"}
                        opacity={`${hoveredIndex === index ? 1 : 0}`}
                        marginRight={"1.5em"}
                      >
                        {/* <IconButton
                          isDisabled={isDisabled}
                          aria-label=""
                          onClick={() => {
                            handleDeleteRow(part);
                          }}
                          backgroundColor={"#BBE6E3"}
                          size={"xs"}
                          height={"20px"}
                        >
                          <DeleteIcon
                            style={{ fontSize: "1.3em" }}
                          ></DeleteIcon>
                        </IconButton> */}
                      </Td>
                    </Tr>
                  );
                });
              })()}
          </Tbody>
        </Table>
      </Box>
      <FacetedPartSearch
        isOpen={isFacetedPartSearchOpen}
        onClose={onFacetedPartSearchClose}
        orderItems={orderItems}
        onAddPart={handleAddPartFromPartSearch_2_0}
        remove_idFromPartDetails={remove_idFromPartDetails}
      ></FacetedPartSearch>
      <GaraazPartConversionModal
        refetchAllOrders={refetchAllOrders}
        isDisabled={isDisabled}
        isOpen={isOpen}
        onClose={onClose}
        orderId={orderId}
        remove_idFromPartDetails={remove_idFromPartDetails}
        orderData={orderData}
        isPartEditDisabled={isPartEditDisabled}
        handleQuanityValueChange={handleQuanityValueChange}
      ></GaraazPartConversionModal>
      <PartActionModal
        isOpen={isCancelHoldModalOpen}
        onClose={() => {
          onCancelHoldModalClose();
          setActionMode(null);
        }}
        isDisabled={false}
        orderId={orderId}
        orderNo={orderData?.orderNo || ""}
        mode={actionMode}
        refetchAllOrders={refetchAllOrders}
      />
      <AddPartQuantityModal
        isOpen={isQuantityModalOpen}
        onClose={() => {
          onQuantityModalClose();
          setSelectedPartIndex(null);
          setQuantity("1");
        }}
        selectedPart={
          selectedPartIndex !== null && parts ? parts[selectedPartIndex] : null
        }
        quantity={quantity}
        onQuantityChange={setQuantity}
        onConfirm={handleAddPartWithQuantity}
        remove_idFromPartDetails={remove_idFromPartDetails}
        isLoading={isSubmitting}
      />
    </Box>
  );
};

export default GaraazPartConversion;
